/**
 * Socket.io connection hook for the web app.
 * Manages connection lifecycle, auth, and event listeners.
 * Uses socket.io-client and syncs state to Zustand stores.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore, useRoomStore, useGameStore } from '../stores';
import type { RoomPlayer, ReconnectPayload } from '@callbridge/ui';

// ─── Server Event Payloads (matching server types/socket.ts) ────

interface RoomUpdatePayload {
  roomCode: string;
  players: Array<{ id: string; name: string; isReady: boolean; isHost: boolean; isAI: boolean }>;
  maxPlayers: number;
  isPrivate: boolean;
}

interface GameStartPayload {
  gameId: string;
  players: Array<{ id: string; name: string; position: number; isAI: boolean }>;
}

interface RoundEndPayload {
  roundNumber: number;
  results: Array<{
    playerId: string;
    call: number;
    tricksWon: number;
    roundScore: number;
    totalScore: number;
  }>;
}

interface GameOverPayload {
  winnerId: string;
  finalScores: Record<string, number>;
}

// ─── Socket Singleton ───────────────────────────────────────────

let socketInstance: Socket | null = null;

function getSocket(token: string): Socket {
  if (socketInstance?.connected) return socketInstance;

  socketInstance?.disconnect();

  const serverUrl = import.meta.env.VITE_SERVER_URL || window.location.origin;
  socketInstance = io(serverUrl, {
    path: '/socket.io',
    auth: { token },
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    // Retry indefinitely with fast back-off (500ms → 5s) so a brief mobile
    // network blip reconnects in seconds rather than the default 30s cap.
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    // Fail the connection attempt after 10s rather than waiting the default
    // 20s, giving the retry loop a chance to try sooner.
    timeout: 10_000,
  });

  return socketInstance;
}

export function getSocketInstance(): Socket | null {
  return socketInstance;
}

// ─── Hook ───────────────────────────────────────────────────────

export function useSocket() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const socketRef = useRef<Socket | null>(null);
  const connectedRef = useRef(false);
  // Tracks whether this socket has connected at least once.
  // Used to distinguish the initial connect from auto-reconnects.
  const hasConnectedRef = useRef(false);
  const navigate = useNavigate();

  // Room store actions
  const roomStore = useRoomStore();
  // Game store actions
  const gameStore = useGameStore();

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const socket = getSocket(accessToken);
    socketRef.current = socket;

    // ─── Connection Events ─────────────────────────────────
    socket.on('connect', () => {
      const isReconnect = hasConnectedRef.current;
      hasConnectedRef.current = true;
      connectedRef.current = true;

      if (!isReconnect) return;

      // Socket.io Connection State Recovery successfully replayed all missed
      // events and restored room membership automatically — no manual sync needed.
      if (socket.recovered) return;

      // Manual reconnect: the server has a brand-new socket object that is
      // NOT in the roomCode Socket.io room. Re-joining is critical — all
      // game:* events go to io.to(roomCode) and will be missed until the
      // socket is back in the room.
      const { gameId } = useGameStore.getState();
      const { roomCode: storedRoomCode } = useRoomStore.getState();

      if (gameId) {
        socket.emit('game:reconnect', { gameId }, (res: SocketResponse<ReconnectPayload>) => {
          if (res.success && res.data) {
            gameStore.reconnectedToGame(res.data);
          } else if (storedRoomCode) {
            // Game ended while disconnected — at least get back into the room
            socket.emit('room:rejoin', (rejoined: SocketResponse<RoomUpdatePayload>) => {
              if (rejoined.success && rejoined.data) {
                roomStore.playersUpdated(
                  rejoined.data.players.map((p) => ({
                    id: p.id,
                    name: p.name,
                    isHost: p.isHost,
                    isReady: p.isReady,
                    isAI: p.isAI,
                  })),
                );
              }
            });
          }
        });
      } else if (storedRoomCode) {
        socket.emit('room:rejoin', (res: SocketResponse<RoomUpdatePayload>) => {
          if (res.success && res.data) {
            roomStore.playersUpdated(
              res.data.players.map((p) => ({
                id: p.id,
                name: p.name,
                isHost: p.isHost,
                isReady: p.isReady,
                isAI: p.isAI,
              })),
            );
          }
        });
      }
    });

    socket.on('disconnect', (_reason) => {
      connectedRef.current = false;
    });

    socket.on('connect_error', (_err) => {
      connectedRef.current = false;
    });

    // ─── Room Events ───────────────────────────────────────
    socket.on('room:updated', (data: RoomUpdatePayload) => {
      const players: RoomPlayer[] = data.players.map((p) => ({
        id: p.id,
        name: p.name,
        isHost: p.isHost,
        isReady: p.isReady,
        isAI: p.isAI,
      }));
      roomStore.playersUpdated(players);
    });

    socket.on('room:playerJoined', (data) => {
      roomStore.playerAdded({
        id: data.playerId,
        name: data.playerName,
        isHost: false,
        isReady: false,
        isAI: false,
      });
    });

    socket.on('room:playerLeft', (data) => {
      roomStore.playerRemoved(data.playerId);
    });

    socket.on('room:playerReady', (data) => {
      roomStore.playerReadyChanged(data.playerId, data.isReady);
    });

    socket.on('room:aiAdded', (data) => {
      roomStore.aiPlayerAdded(data.playerId, data.difficulty);
    });

    socket.on('room:playerKicked', (data) => {
      if (data.playerId === useRoomStore.getState().localPlayerId) {
        roomStore.left();
      }
    });

    socket.on('room:gameStarting', () => {
      roomStore.roomStarting();
    });

    // ─── Game Events ───────────────────────────────────────
    socket.on('game:started', (data: GameStartPayload) => {
      const userId = useAuthStore.getState().user?.id ?? '';
      const localIndex = data.players.findIndex((p) => p.id === userId);
      const players = data.players.map((p) => ({
        id: p.id,
        name: p.name,
        seatIndex: p.position,
        isAI: p.isAI,
        isConnected: true,
        call: null,
        tricksWon: 0,
        totalScore: 0,
      }));
      gameStore.gameStarted(data.gameId, players, localIndex);

      const roomCode = useRoomStore.getState().roomCode;
      if (roomCode) {
        navigate(`/game/${roomCode}?mode=playing`);
      }
    });

    socket.on('game:hand', (data) => {
      gameStore.handReceived(data.cards);
    });

    socket.on('game:voids', (data) => {
      gameStore.voidsReceived(data.voids);
    });

    socket.on('game:callPhase', (data) => {
      const players = useGameStore.getState().players;
      const idx = players.findIndex((p) => p.id === data.currentPlayerId);
      gameStore.callPhaseStarted(idx >= 0 ? idx : 0);
    });

    socket.on('game:callMade', (data) => {
      gameStore.callMade(data.playerId, data.call);
    });

    socket.on('game:playPhase', (data) => {
      const players = useGameStore.getState().players;
      const idx = players.findIndex((p) => p.id === data.currentPlayerId);
      gameStore.playPhaseStarted(idx >= 0 ? idx : 0);
    });

    socket.on('game:cardPlayed', (data) => {
      gameStore.cardPlayed(data.playerId, data.card);
    });

    socket.on('game:trickWon', (data) => {
      gameStore.trickWon(data.winnerId);
    });

    socket.on('game:roundEnd', (data: RoundEndPayload) => {
      gameStore.roundEnded(data.results);
    });

    socket.on('game:roundStarted', (data) => {
      gameStore.newRoundStarted(data.roundNumber, data.dealerIndex);
    });

    socket.on('game:over', (data: GameOverPayload) => {
      gameStore.gameEnded(data.winnerId, data.finalScores);
    });

    socket.on('game:abandoned', (_data) => {
      gameStore.gameAbandoned();
    });

    socket.on('game:turnTimer', (data) => {
      gameStore.turnTimerUpdated(data.timeRemainingMs);
    });

    socket.on('game:turnTimeout', (_data) => {
      gameStore.turnTimerUpdated(0);
    });

    // ─── Player Status Events ──────────────────────────────
    socket.on('player:disconnected', (data) => {
      gameStore.playerDisconnected(data.playerId);
    });

    socket.on('player:reconnected', (data) => {
      gameStore.playerReconnected(data.playerId);
    });

    socket.on('player:replacedByAI', (data) => {
      gameStore.playerReplacedByAI(data.playerId);
    });

    // ─── Error Events ──────────────────────────────────────
    socket.on('error', (data) => {
      gameStore.errorSet(data.message);
    });

    // Connect
    socket.connect();

    return () => {
      hasConnectedRef.current = false;
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('room:updated');
      socket.off('room:playerJoined');
      socket.off('room:playerLeft');
      socket.off('room:playerReady');
      socket.off('room:aiAdded');
      socket.off('room:playerKicked');
      socket.off('room:gameStarting');
      socket.off('game:started');
      socket.off('game:hand');
      socket.off('game:voids');
      socket.off('game:callPhase');
      socket.off('game:callMade');
      socket.off('game:playPhase');
      socket.off('game:cardPlayed');
      socket.off('game:trickWon');
      socket.off('game:roundEnd');
      socket.off('game:roundStarted');
      socket.off('game:over');
      socket.off('game:abandoned');
      socket.off('game:turnTimer');
      socket.off('game:turnTimeout');
      socket.off('player:disconnected');
      socket.off('player:reconnected');
      socket.off('player:replacedByAI');
      socket.off('error');
      socket.disconnect();
      connectedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  return {
    socket: socketRef.current,
    isConnected: connectedRef.current,
  };
}

// ─── Callback Type ──────────────────────────────────────────────

interface SocketResponse<T = void> {
  success: boolean;
  data?: T;
  error?: string;
  existingRoomCode?: string;
}

// ─── Errors ──────────────────────────────────────────────────────

export class RoomConflictError extends Error {
  existingRoomCode?: string;

  constructor(message: string, existingRoomCode?: string) {
    super(message);
    this.name = 'RoomConflictError';
    this.existingRoomCode = existingRoomCode;
  }
}

// ─── Imperative Helpers ─────────────────────────────────────────

export function emitCreateRoom(isPrivate: boolean, maxPlayers = 4): Promise<{ roomCode: string }> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit(
      'room:create',
      { isPrivate, maxPlayers },
      (res: SocketResponse<{ roomCode: string }>) => {
        if (res.success && res.data) resolve(res.data);
        else
          reject(new RoomConflictError(res.error ?? 'Failed to create room', res.existingRoomCode));
      },
    );
  });
}

export function emitJoinRoom(roomCode: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:join', { roomCode }, (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new RoomConflictError(res.error ?? 'Failed to join room', res.existingRoomCode));
    });
  });
}

export function emitRejoinRoom(): Promise<RoomUpdatePayload> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:rejoin', (res: SocketResponse<RoomUpdatePayload>) => {
      if (res.success && res.data) resolve(res.data);
      else reject(new Error(res.error ?? 'Failed to rejoin room'));
    });
  });
}

export function emitLeaveRoom(): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:leave', (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to leave room'));
    });
  });
}

export function emitToggleReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:ready', (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to toggle ready'));
    });
  });
}

export function emitKickPlayer(playerId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:kick', { playerId }, (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to kick player'));
    });
  });
}

export function emitAddAI(difficulty: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:addAI', { difficulty }, (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to add AI'));
    });
  });
}

export function emitStartGame(): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('room:start', (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to start game'));
    });
  });
}

export function emitCall(call: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('game:call', { call }, (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to make call'));
    });
  });
}

export function emitPlayCard(suit: string, rank: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocketInstance();
    if (!socket?.connected) return reject(new Error('Not connected'));

    socket.emit('game:playCard', { suit, rank }, (res: SocketResponse) => {
      if (res.success) resolve();
      else reject(new Error(res.error ?? 'Failed to play card'));
    });
  });
}

export function emitChatMessage(message: string): void {
  const socket = getSocketInstance();
  if (!socket?.connected) return;

  socket.emit('chat:message', { message });
}

export function emitSubscribeToLobby(): void {
  const socket = getSocketInstance();
  if (!socket?.connected) return;
  socket.emit('room:subscribe');
}

export function emitUnsubscribeFromLobby(): void {
  const socket = getSocketInstance();
  if (!socket?.connected) return;
  socket.emit('room:unsubscribe');
}

export interface PublicRoomEntry {
  code: string;
  hostName: string;
  playerCount: number;
  maxPlayers: number;
  createdAt: number;
}
